import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, Search, ExternalLink, AlertCircle, CheckCircle2, Mail, ShieldAlert } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin → Settings card with two invoice-resend actions:
 *   1. Resend WhatsApp (existing behaviour) — for any DELIVERED booking
 *   2. Email GST invoice PDF — the new GST-compliant tax-invoice email
 *
 * Also shows a bright warning banner when the business/GSTIN config is
 * still the DUMMY placeholder — reminds staff to swap real values into
 * backend/.env before going live.
 */
export const InvoiceResendCard = () => {
  const [tag, setTag] = useState('');
  const [bookingId, setBookingId] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [waLoading, setWaLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [emailResult, setEmailResult] = useState(null);
  const [error, setError] = useState('');
  const [gstStatus, setGstStatus] = useState(null);

  useEffect(() => {
    axios.get(`${API}/config/gst-status`)
      .then(r => setGstStatus(r.data))
      .catch(() => setGstStatus(null));
  }, []);

  const onResendWA = async (e) => {
    e?.preventDefault();
    const clean = (tag || '').trim().toUpperCase();
    if (!clean) { setError('Please enter a baggage tag (e.g. LFJ164732)'); return; }
    setWaLoading(true); setError(''); setResult(null); setEmailResult(null);
    try {
      const res = await axios.post(`${API}/admin/invoices/resend/${encodeURIComponent(clean)}`, null);
      setResult(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not resend invoice via WhatsApp');
    } finally { setWaLoading(false); }
  };

  const onEmailGst = async () => {
    const bid = (bookingId || '').trim();
    if (!bid) { setError('Enter the booking ID (uuid) to email the GST invoice'); return; }
    setEmailLoading(true); setError(''); setEmailResult(null); setResult(null);
    try {
      const res = await axios.post(`${API}/admin/bookings/${encodeURIComponent(bid)}/invoice/email`);
      setEmailResult(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not email GST invoice');
    } finally { setEmailLoading(false); }
  };

  return (
    <div
      data-testid="invoice-resend-card"
      className="rounded-2xl border border-[#1A1A1A]/10 bg-white p-6 shadow-sm space-y-5"
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-[#FFF1E8] flex items-center justify-center flex-shrink-0">
          <FileText size={20} className="text-[#FF5A1F]" />
        </div>
        <div>
          <h3 className="text-base font-bold text-[#1A1A1A]" style={{ fontFamily: 'Cabinet Grotesk' }}>
            Invoice resender
          </h3>
          <p className="text-xs text-[#737373] mt-0.5">
            Re-send WhatsApp or email a GST-compliant PDF for any DELIVERED booking.
          </p>
        </div>
      </div>

      {/* DUMMY GSTIN warning banner */}
      {gstStatus?.is_dummy && (
        <div
          data-testid="gst-dummy-warning"
          className="flex items-start gap-3 rounded-xl bg-[#FEF3C7] border border-[#FCD34D] p-3 text-sm"
        >
          <ShieldAlert size={18} className="text-[#B45309] flex-shrink-0 mt-0.5" />
          <div className="text-[#78350F]">
            <div className="font-bold">Invoice config is DUMMY — replace before going live.</div>
            <div className="text-xs mt-1">
              Current GSTIN: <code className="bg-white/70 px-1 rounded">{gstStatus.gstin}</code>,
              Legal name: <code className="bg-white/70 px-1 rounded">{gstStatus.legal_name}</code>.
              Update <code>BUSINESS_*</code> values in <code>backend/.env</code> then restart backend.
            </div>
          </div>
        </div>
      )}

      {/* Resend via WhatsApp */}
      <form onSubmit={onResendWA} className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1A1A1A]/40" />
          <input
            data-testid="invoice-resend-input"
            type="text"
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            placeholder="Baggage tag (e.g. LFJ164732)"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-[#FAF6EE] border border-[#1A1A1A]/15 text-[#1A1A1A] uppercase tracking-wider text-sm placeholder:text-[#1A1A1A]/30 outline-none focus:border-[#FF5A1F] focus:bg-white focus:ring-2 focus:ring-[#FF5A1F]/15 transition"
          />
        </div>
        <button
          data-testid="invoice-resend-btn"
          type="submit"
          disabled={waLoading || !tag.trim()}
          className="px-5 py-2.5 rounded-full bg-[#FF5A1F] text-white text-sm font-semibold uppercase tracking-wide hover:bg-[#E64200] disabled:opacity-50 disabled:cursor-not-allowed transition whitespace-nowrap"
        >
          {waLoading ? 'SENDING…' : 'RESEND WHATSAPP'}
        </button>
      </form>

      {/* Email GST invoice */}
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          data-testid="invoice-email-booking-input"
          type="text"
          value={bookingId}
          onChange={(e) => setBookingId(e.target.value)}
          placeholder="Booking ID (uuid) to email GST invoice PDF"
          className="flex-1 px-3 py-2.5 rounded-xl bg-[#FAF6EE] border border-[#1A1A1A]/15 text-[#1A1A1A] font-mono text-xs placeholder:text-[#1A1A1A]/30 outline-none focus:border-[#FF5A1F] focus:bg-white focus:ring-2 focus:ring-[#FF5A1F]/15 transition"
        />
        <button
          data-testid="invoice-email-btn"
          type="button"
          onClick={onEmailGst}
          disabled={emailLoading || !bookingId.trim()}
          className="px-5 py-2.5 rounded-full bg-black text-white text-sm font-semibold uppercase tracking-wide hover:bg-[#1A1A1A] disabled:opacity-50 disabled:cursor-not-allowed transition whitespace-nowrap flex items-center gap-2 justify-center"
        >
          <Mail size={14} />{emailLoading ? 'SENDING…' : 'EMAIL GST INVOICE'}
        </button>
      </div>

      {error && (
        <div data-testid="invoice-resend-error" className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-xl p-3">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div data-testid="invoice-resend-result" className="rounded-xl bg-[#F0FDF4] border border-[#86EFAC] p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-[#166534]">
            <CheckCircle2 size={16} /> Invoice {result.invoice_number} sent to customer
          </div>
          <div className="text-xs text-[#525252] space-y-1">
            <div><span className="text-[#737373]">Baggage tag:</span> <strong>{result.baggage_tag}</strong></div>
            <div><span className="text-[#737373]">WhatsApp:</span> {result.wa_result?.ok ? 'delivered ✓' : 'failed ✗'}</div>
          </div>
          {result.invoice_url && (
            <a data-testid="invoice-download-link" href={result.invoice_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#FF5A1F] hover:underline">
              View / download PDF <ExternalLink size={12} />
            </a>
          )}
        </div>
      )}

      {emailResult && (
        <div data-testid="invoice-email-result" className="rounded-xl bg-[#F0FDF4] border border-[#86EFAC] p-4 text-sm text-[#166534] font-semibold flex items-center gap-2">
          <CheckCircle2 size={16} /> GST invoice {emailResult.invoice_number} emailed to {emailResult.sent_to}
        </div>
      )}
    </div>
  );
};

export default InvoiceResendCard;
